import clsx from "clsx";
import { Mic, Square } from "lucide-react";

import type { DictationStatus } from "@/hooks/useDictation";

interface DictationButtonProps {
  variant: "cyan" | "magenta";
  status: DictationStatus;
  isSupported: boolean;
  disabled?: boolean;
  onClick: () => void;
  onPointerDown?: React.PointerEventHandler<HTMLButtonElement>;
}

function getButtonLabel(status: DictationStatus, isSupported: boolean): string {
  if (!isSupported) {
    return "Dictation is not supported in this browser";
  }

  if (status === "recording") {
    return "Stop recording";
  }

  if (status === "transcribing") {
    return "Transcribing...";
  }

  if (status === "processing") {
    return "Processing...";
  }

  return "Start recording";
}

export function DictationButton({
  variant,
  status,
  isSupported,
  disabled,
  onClick,
  onPointerDown,
}: DictationButtonProps): JSX.Element {
  const label = getButtonLabel(status, isSupported);
  const isDisabled =
    disabled || !isSupported || status === "transcribing" || status === "processing";

  const hoverColor =
    variant === "cyan"
      ? "hover:border-cyber-cyan/50 hover:text-cyber-cyan"
      : "hover:border-cyber-magenta/50 hover:text-cyber-magenta";

  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={onPointerDown}
      disabled={isDisabled}
      className={clsx(
        "inline-flex h-9 w-9 items-center justify-center rounded-full border transition-all",
        isDisabled
          ? "cursor-not-allowed border-cyber-border bg-cyber-surface/40 text-cyber-muted/60"
          : "border-cyber-border text-cyber-muted",
        !isDisabled && status === "idle" && hoverColor,
        status === "recording" &&
          "animate-neon-pulse border-cyber-red/70 bg-cyber-red/10 text-cyber-red hover:border-cyber-red hover:text-cyber-red",
        (status === "transcribing" || status === "processing") &&
          "border-cyber-border bg-cyber-surface/60 text-cyber-muted opacity-90",
      )}
      aria-label={label}
      title={label}
    >
      {status === "recording" ? (
        <Square size={12} />
      ) : status === "transcribing" || status === "processing" ? (
        <span className="animate-spin-glow inline-block">◐</span>
      ) : (
        <Mic size={16} />
      )}
    </button>
  );
}
